import React, { useState, useEffect, useRef, useCallback } from 'react'
import { useParams, Link } from 'react-router-dom'
import { Card, Button, Form, Alert, Spinner } from 'react-bootstrap'
import { chatsAPI } from '../../services/chatsApi'
import { getConsumer } from '../../services/cable'
import ChatSectionHeader from './ChatSectionHeader'

const SENDER_COLORS = ['#0d6efd', '#dc3545', '#198754', '#fd7e14', '#6f42c1', '#0dcaf0', '#d63384', '#795548']

const senderColor = (userId) => SENDER_COLORS[userId % SENDER_COLORS.length]

const formatTime = (timestamp) =>
  new Date(timestamp).toLocaleString('ru-RU', {
    day: '2-digit',
    month: '2-digit',
    hour: '2-digit',
    minute: '2-digit',
  })

const ChatMessages = ({ currentUser }) => {
  const { id } = useParams()
  const [chat, setChat] = useState(null)
  const [messages, setMessages] = useState([])
  const [body, setBody] = useState('')
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState(null)
  const [sending, setSending] = useState(false)
  const messagesContainerRef = useRef(null)

  const appendMessage = useCallback((message) => {
    setMessages((prev) => {
      if (prev.some((m) => m.id === message.id)) return prev
      return [...prev, message]
    })
  }, [])

  const loadMessages = useCallback(async () => {
    try {
      setLoading(true)
      const response = await chatsAPI.messages(id)
      setChat(response.data.chat)
      setMessages(response.data.messages)
      setError(null)
      window.dispatchEvent(new Event('chats:updated'))
    } catch (err) {
      console.error('Error loading messages:', err)
      setError(err.response?.status === 403 ? 'У вас нет доступа к этому чату' : 'Не удалось загрузить чат')
    } finally {
      setLoading(false)
    }
  }, [id])

  useEffect(() => {
    loadMessages()
  }, [loadMessages])

  useEffect(() => {
    const subscription = getConsumer().subscriptions.create(
      { channel: 'ChatChannel', chat_id: id },
      {
        received: (data) => {
          if (data.type !== 'message') return

          appendMessage(data.message)
          if (currentUser && data.message.user.id !== currentUser.id) {
            chatsAPI.markRead(id).then(() => window.dispatchEvent(new Event('chats:updated')))
          }
        },
      }
    )

    return () => subscription.unsubscribe()
  }, [id, appendMessage, currentUser])

  useEffect(() => {
    const container = messagesContainerRef.current
    if (container) {
      container.scrollTop = container.scrollHeight
    }
  }, [messages])

  const handleSend = async (e) => {
    e.preventDefault()
    const text = body.trim()
    if (!text) return

    try {
      setSending(true)
      const response = await chatsAPI.sendMessage(id, text)
      appendMessage(response.data)
      setBody('')
    } catch (err) {
      console.error('Error sending message:', err)
      setError('Не удалось отправить сообщение')
    } finally {
      setSending(false)
    }
  }

  if (loading) {
    return (
      <div className="text-center py-5">
        <Spinner animation="border" />
      </div>
    )
  }

  if (error && !chat) {
    return <Alert variant="danger">{error}</Alert>
  }

  return (
    <div>
      <ChatSectionHeader
        title="Чат"
        subtitle={
          <Link to={`/projects/${chat.project_id}`} className="text-muted text-decoration-none">
            {chat.name} · {chat.project_address}
          </Link>
        }
        backTo={`/chats/${id}`}
      />

      {error && <Alert variant="danger" dismissible onClose={() => setError(null)}>{error}</Alert>}

      <Card className="shadow-sm border-0">
        <Card.Body ref={messagesContainerRef} className="p-3 chat-messages">
          {messages.length === 0 && (
            <div className="text-muted text-center py-5">Сообщений пока нет</div>
          )}
          {messages.map((message) => {
            const isOwn = currentUser && message.user.id === currentUser.id
            return (
              <div
                key={message.id}
                className={`d-flex mb-2 ${isOwn ? 'justify-content-end' : 'justify-content-start'}`}
              >
                <div
                  className={`rounded p-2 px-3 ${isOwn ? 'bg-primary text-white' : 'bg-light'}`}
                  style={{ maxWidth: '75%' }}
                >
                  <div
                    className="small fw-semibold"
                    style={isOwn ? undefined : { color: senderColor(message.user.id) }}
                  >
                    {isOwn ? 'Вы' : message.user.email}
                  </div>
                  <div style={{ whiteSpace: 'pre-wrap', wordBreak: 'break-word' }}>{message.body}</div>
                  <div className={`small ${isOwn ? 'text-white-50' : 'text-muted'}`}>
                    {formatTime(message.created_at)}
                  </div>
                </div>
              </div>
            )
          })}
        </Card.Body>
        <Card.Footer className="bg-white p-3">
          <Form onSubmit={handleSend} className="d-flex gap-2">
            <Form.Control
              type="text"
              placeholder="Написать сообщение..."
              value={body}
              onChange={(e) => setBody(e.target.value)}
            />
            <Button variant="primary" type="submit" disabled={sending || !body.trim()}>
              Отправить
            </Button>
          </Form>
        </Card.Footer>
      </Card>
    </div>
  )
}

export default ChatMessages
